import { useState, useEffect } from "react";
import moment from "moment";

/**
 * Custom hook for countdown timer functionality
 * @param startTime - ISO string or Date for countdown target
 * @param format - Output format: "mm:ss", "hh:mm:ss" (default) or "verbose"
 * @returns Formatted countdown string
 */
export const useCountdown = (
  startTime?: string | Date,
  format: "mm:ss" | "hh:mm:ss" | "verbose" = "hh:mm:ss"
): string => {
  const [countdown, setCountdown] = useState(
    format === "verbose" ? "Starting soon..." : format === "hh:mm:ss" ? "00:00:00" : "00:00"
  );

  useEffect(() => {
    if (!startTime) return;

    const updateCountdown = () => {
      const now = moment();
      const target = moment(startTime);
      const duration = moment.duration(target.diff(now));
      
      const days = Math.floor(duration.asDays());
      const hours = Math.floor(duration.asHours() % 24);
      const minutes = Math.floor(duration.asMinutes() % 60);
      const seconds = Math.floor(duration.asSeconds() % 60);
      
      // If time has passed
      if (duration.asSeconds() <= 0) {
        if (format === "hh:mm:ss") {
          setCountdown("00:00:00");
        } else if (format === "mm:ss") {
          setCountdown("00:00");
        } else {
          setCountdown("Started");
        }
        return false; // Stop the interval
      }

      if (format === "hh:mm:ss") {
        // hh:mm:ss format
        const totalHours = Math.floor(duration.asHours());
        setCountdown(
          `${totalHours.toString().padStart(2, "0")}:${minutes
            .toString()
            .padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`
        );
      } else if (format === "mm:ss") {
        // Simple mm:ss format
        const totalMinutes = Math.floor(duration.asMinutes());
        setCountdown(
          `${totalMinutes.toString().padStart(2, "0")}:${seconds
            .toString()
            .padStart(2, "0")}`
        );
      } else {
        // Verbose format with days/hours/minutes
        if (days > 0) {
          setCountdown(`${days}d ${hours}h ${minutes}m`);
        } else if (hours > 0) {
          setCountdown(`${hours}h ${minutes}m ${seconds}s`);
        } else if (minutes > 0) {
          setCountdown(`${minutes}m ${seconds}s`);
        } else {
          setCountdown(`${seconds}s`);
        }
      }
      
      return true; // Continue the interval
    };

    // Initial update
    const shouldContinue = updateCountdown();
    if (!shouldContinue) return;

    // Set up interval
    const interval = setInterval(() => {
      const shouldContinue = updateCountdown();
      if (!shouldContinue) {
        clearInterval(interval);
      }
    }, 1000);

    return () => clearInterval(interval);
  }, [startTime, format]);

  return countdown;
};

/**
 * Get time until a specific point (e.g., 5 minutes before event)
 * @param eventTime - The event time
 * @param minutesBefore - Minutes before the event to check
 * @returns Object with time info
 */
export const getTimeUntilEvent = (
  eventTime?: string | Date,
  minutesBefore: number = 0
) => {
  if (!eventTime) {
    return {
      isPast: false,
      isWithinWindow: false,
      millisecondsUntil: Number.MAX_SAFE_INTEGER,
      secondsUntil: Number.MAX_SAFE_INTEGER,
      minutesUntil: Number.MAX_SAFE_INTEGER,
    };
  }

  const now = new Date();
  const target = new Date(eventTime);
  const windowTime = new Date(target.getTime() - minutesBefore * 60 * 1000);
  
  const millisecondsUntilWindow = windowTime.getTime() - now.getTime();
  const millisecondsUntilEvent = target.getTime() - now.getTime();
  
  return {
    isPast: millisecondsUntilEvent <= 0,
    isWithinWindow: millisecondsUntilWindow <= 0,
    millisecondsUntil: millisecondsUntilEvent,
    secondsUntil: Math.floor(millisecondsUntilEvent / 1000),
    minutesUntil: Math.floor(millisecondsUntilEvent / (1000 * 60)),
  };
};
